import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 5;

export function WebSocketProvider({ children }) {
    const { token, user } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    
    const wsRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const reconnectTimeoutRef = useRef(null);
    const listenersRef = useRef(new Map());

    // Get WebSocket URL - only use WebSocket for local development
    const getWsUrl = useCallback(() => {
        if (!token) return null;
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        // Check if we're in a preview/production environment
        // WebSocket may not work through external proxy
        if (backendUrl.includes('preview.emergentagent.com') || backendUrl.includes('.vercel.app')) {
            console.log('WebSocket disabled in preview environment, using polling');
            return null;
        }
        
        // Convert http(s) to ws(s) and remove /api suffix if present
        const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
        // Remove protocol and /api suffix, keep just the host
        let wsHost = backendUrl
            .replace(/^https?:\/\//, '')
            .replace(/\/api\/?$/, '')
            .replace(/\/$/, '');
        
        const wsUrl = `${wsProtocol}://${wsHost}/ws/${token}`;
        console.log('WebSocket URL:', wsUrl);
        return wsUrl;
    }, [token]);

    // Subscribe to message types
    const subscribe = useCallback((messageType, callback) => {
        if (!listenersRef.current.has(messageType)) {
            listenersRef.current.set(messageType, new Set());
        }
        listenersRef.current.get(messageType).add(callback);
        
        return () => {
            listenersRef.current.get(messageType)?.delete(callback);
        };
    }, []);

    // Notify listeners
    const notifyListeners = useCallback((message) => {
        const type = message?.type;
        if (type && listenersRef.current.has(type)) {
            listenersRef.current.get(type).forEach(callback => {
                try {
                    callback(message);
                } catch (e) {
                    console.error('WebSocket listener error:', e);
                }
            });
        }
    }, []);

    // Connect to WebSocket
    const connect = useCallback(() => {
        const wsUrl = getWsUrl();
        if (!wsUrl || wsRef.current?.readyState === WebSocket.OPEN) {
            return;
        }

        if (user?.role === 'master') {
            return;
        }

        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('WebSocket connected');
                setIsConnected(true);
                reconnectAttemptsRef.current = 0;
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    
                    // Handle different message types
                    if (message.type === 'device_update') {
                        setLastUpdate({
                            type: 'device_update',
                            deviceId: message.device_id,
                            deviceName: message.device_name,
                            measurementType: message.measurement_type,
                            online: message.online,
                            reading: message.reading,
                            timestamp: new Date()
                        });
                    } else if (message.type === 'device_status') {
                        setLastUpdate({
                            type: 'device_status',
                            deviceId: message.device_id,
                            deviceName: message.device_name,
                            online: message.online,
                            timestamp: new Date()
                        });
                    } else if (message.type === 'ping') {
                        ws.send(JSON.stringify({ type: 'pong' }));
                    }
                    
                    notifyListeners(message);
                } catch (e) {
                    console.error('Failed to parse WebSocket message:', e);
                }
            };

            ws.onclose = (event) => {
                console.log('WebSocket closed:', event.code);
                setIsConnected(false);
                wsRef.current = null;

                if (event.code !== 1000 && reconnectAttemptsRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
                    reconnectAttemptsRef.current++;
                    reconnectTimeoutRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

        } catch (error) {
            console.error('Failed to create WebSocket:', error);
        }
    }, [getWsUrl, notifyListeners, user?.role]);

    // Disconnect
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        
        if (wsRef.current) {
            wsRef.current.close(1000, 'User disconnect');
            wsRef.current = null;
        }
        
        setIsConnected(false);
        reconnectAttemptsRef.current = 0;
    }, []);

    // Auto-connect when authenticated
    useEffect(() => {
        if (token && user?.role !== 'master') {
            // Small delay to ensure token is ready
            const timeout = setTimeout(connect, 500);
            return () => clearTimeout(timeout);
        } else {
            disconnect();
        }
        
        return () => disconnect();
    }, [token, user?.role, connect, disconnect]);

    // Ping to keep alive
    useEffect(() => {
        if (!isConnected) return;
        
        const pingInterval = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'ping' }));
            }
        }, 25000);
        
        return () => clearInterval(pingInterval);
    }, [isConnected]);

    const value = {
        isConnected,
        lastUpdate,
        subscribe
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
}

export function useWebSocket() {
    const context = useContext(WebSocketContext);
    if (!context) {
        // Return a default value if not within provider (for master users)
        return {
            isConnected: false,
            lastUpdate: null,
            subscribe: () => () => {}
        };
    }
    return context;
}

export default WebSocketContext;
